import React, { useEffect, useState, useMemo } from "react";
import { Box, Button, Stack, Typography, ToggleButton, ToggleButtonGroup, Chip } from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ShortsModal from "./components/shortModal";
import ShortTable from "./components/shortTable";
import { useDispatch } from "react-redux";
import { useAppDispatch, useAppSelector } from "store";
import { TRIP_STATUS } from "utils/index";
import { fetchTrips } from "slice/tripSlice";
import { fetchShorts, forceRefreshShorts, resetRefetchFlag } from "slice/shortSlice";
import { AppDispatch, RootState } from "store";
import { Trips } from "../trips-created/types/types";
import { deleteMemories, updateMemoriesById, updateMemoriesStaus } from "services/shortService";
import { toast } from "react-toastify";

const ShortScreen: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const userDetails = useAppSelector(
    (state: RootState) => state.appLayout.userDetails
  ) as any;
  const tripCache = useAppSelector((state: RootState) => state.trips.tripCache);
  const tripStatus = useAppSelector((state: RootState) => state.trips.status);
  const shortState = useAppSelector((state: RootState) => state.shorts);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>("In-Review");

  const merchantId = userDetails?.userId; 

  const statusOptions = ["In-Review", "Approved", "Rejected"];

  // Get loading state for APPROVED trips
  const isTripsLoading = tripStatus[TRIP_STATUS.APPROVED] === "loading";

  // Get loading state for shorts
  const isShortsLoading = shortState.status === "loading";

  const handleStatusChange = (
    _event: React.MouseEvent<HTMLElement>,
    newStatus: string | null
  ) => {
    if (newStatus !== null) {
      setSelectedStatus(newStatus);
      // Refetch shorts immediately with new status (no caching)
      dispatch(fetchShorts({ merchantId, status: newStatus }));
    }
  };

  const handleDeleteShort = async (shortId?: string, shortItem?: any) => {
    if (!shortId) {
      console.error("Short ID is missing");
      return;
    }
    try {
      const data = {
        filePath: "memories/" + `${shortItem?.id}-memories-${shortItem?.thumbnailAltText}.mp4`,
        thumbnailName: "thumbnails/" + `${shortItem?.id}-thumbnail-${shortItem?.thumbnailAltText}.jpg`,
      };
      
      // Delete the memories files
      await deleteMemories(data);
      
      // Update the status to Deleted
      await updateMemoriesStaus(shortId, "Deleted");
      toast.success("Memories deleted successfully!");
      
      // Trigger a refresh of shorts after deletion and status update
      dispatch(forceRefreshShorts());
    } catch (error) {
      console.error("Error deleting memories:", error);
      toast.error("Error deleting memories. Please try again.");
    }
  };

  const handleUpdateCaption = async (shortId: string, caption: string) => {
    if (!shortId) {
      console.error("Memories ID is missing");
      return;
    }
    try {
      const response = await updateMemoriesById(shortId, { caption });

      if (
        response?.data?.message === "Item updated successfully" ||
        response?.message === "Item updated successfully"
      ) {
        toast.success("Caption updated successfully!");
        // Trigger a refresh of shorts after update
        dispatch(forceRefreshShorts());
      } else {
        throw new Error(response?.data?.message || "Failed to update caption");
      }
    } catch (error: any) {
      console.error("Error updating caption:", error);
      const errorMsg =
        error?.response?.data?.message || error?.message || "Error updating caption";
      toast.error(errorMsg);
    }
  };

  // Fetch approved trips on component mount
  useEffect(() => {
    if (userDetails?.userId && tripCache[TRIP_STATUS.APPROVED] === null) {
      dispatch(
        fetchTrips({
          tripStatus: TRIP_STATUS.APPROVED,
          id: userDetails?.userId,
        })
      );
    }
  }, [userDetails?.userId, tripCache, dispatch]);

  // Fetch shorts once we have merchantId - initial load
  useEffect(() => {
    if (merchantId) {
      dispatch(fetchShorts({ merchantId, status: selectedStatus }));
    }
  }, [merchantId]);

  // Handle force refresh - refetch shorts when shouldRefetch flag is set
  useEffect(() => {
    if (shortState.shouldRefetch && merchantId) {
      dispatch(fetchShorts({ merchantId, status: selectedStatus }));
      dispatch(resetRefetchFlag());
    }
  }, [shortState.shouldRefetch, merchantId, dispatch, selectedStatus]);

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6" gutterBottom>
          Manage Memories
        </Typography>
        <Button
          variant="contained"
          startIcon={<UploadFileIcon />}
          onClick={() => setOpenDialog(true)}
        >
          Create New Memories
        </Button>
      </Stack>

      <ShortTable
        shorts={shortState.shortsByStatus[selectedStatus] || []}
        tripsMap={tripCache[TRIP_STATUS.APPROVED] || []}
        merchant={userDetails}
        onDelete={handleDeleteShort}
        onUpdateCaption={handleUpdateCaption}
        isLoading={isTripsLoading || isShortsLoading}
        selectedStatus={selectedStatus}
        onStatusChange={handleStatusChange}
        statusOptions={statusOptions}
      />
      {/* Create Short Modal */}
      {openDialog && <ShortsModal open={openDialog} setOpen={setOpenDialog} />}
    </Box>
  );
};
export default ShortScreen;
